import React from 'react';
import { Card } from '@roger/ui-components';
import type { EpisodeLupaPeriodResponse } from '../services/api';

type LupaPeriodStatus = EpisodeLupaPeriodResponse['status'];

interface LupaThresholdGaugeProps {
  periods: EpisodeLupaPeriodResponse[];
}

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th'];

// No dedicated success/warning token in the design system, so status colors use
// the Tailwind palette for clear/at-risk and the --destructive token for LUPA.
// Static class strings (no interpolation) so Tailwind keeps them.
const STATUS_STYLE: Record<
  LupaPeriodStatus,
  { tint: string; bar: string; accent: string }
> = {
  clear: {
    tint: 'bg-emerald-50/60',
    bar: 'bg-emerald-600',
    accent: 'text-emerald-700',
  },
  at_risk: {
    tint: 'bg-amber-50/70',
    bar: 'bg-amber-500',
    accent: 'text-amber-700',
  },
  lupa: {
    tint: 'bg-red-50/70',
    bar: 'bg-destructive',
    accent: 'text-destructive',
  },
  unknown: { tint: 'bg-gray-50', bar: 'bg-gray-300', accent: 'text-gray-500' },
};

const ordinalLabel = (n: number): string => ORDINALS[n - 1] ?? `${n}th`;

const statusLabel = (period: EpisodeLupaPeriodResponse): string => {
  if (period.status === 'unknown' || period.lupa_threshold == null) {
    return 'Threshold unavailable';
  }
  const remaining = Math.max(
    0,
    period.lupa_threshold - period.billable_visit_count
  );
  if (period.status === 'clear') {
    return `Clear · ${period.billable_visit_count} of ${period.lupa_threshold}`;
  }
  if (period.status === 'at_risk') {
    return `${remaining} more to clear LUPA`;
  }
  return `LUPA · ${period.billable_visit_count} of ${period.lupa_threshold}`;
};

/**
 * Per-30-day-period LUPA gauge for the episode-manager calendar (ROG-2493).
 *
 * Presentational: each period shows how its billable visit count tracks toward
 * the LUPA threshold, tinted by status — clear (green), at-risk while the
 * period is still open (amber, "N more to clear LUPA"), or a locked LUPA once
 * a closed period fell short (red).
 */
const LupaThresholdGauge: React.FC<LupaThresholdGaugeProps> = ({ periods }) => {
  if (periods.length === 0) {
    return null;
  }
  return (
    <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
      {periods.map(period => {
        const style = STATUS_STYLE[period.status];
        const threshold = period.lupa_threshold ?? 0;
        const fillPct =
          threshold > 0
            ? Math.min(
                100,
                Math.round((period.billable_visit_count / threshold) * 100)
              )
            : 0;
        return (
          <Card
            key={period.period_number}
            className={`${style.tint} p-3 sm:p-3 md:p-3`}
          >
            <div className='mb-2 flex items-center justify-between gap-2'>
              <span className='text-xs font-semibold uppercase tracking-wider text-gray-600'>
                {ordinalLabel(period.period_number)} 30-day period
              </span>
              <span className={`text-xs font-semibold ${style.accent}`}>
                {statusLabel(period)}
              </span>
            </div>
            <div className='h-2 overflow-hidden rounded-full bg-white/70'>
              <div
                className={`h-full rounded-full ${style.bar}`}
                style={{ width: `${fillPct}%` }}
              />
            </div>
            <div className='mt-1.5 text-[0.7rem] text-gray-500'>
              {period.lupa_threshold != null
                ? `${period.billable_visit_count} of ${period.lupa_threshold} billable visits · LUPA threshold ${period.lupa_threshold}`
                : 'No LUPA threshold yet'}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default LupaThresholdGauge;
